/**
 * Auction Service - Interface with the auction smart contract
 */
import { Connection, PublicKey, Transaction, SystemProgram, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import { Program, BN } from '@project-serum/anchor';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { 
  getConnection, 
  getProgram, 
  findPDA, 
  solToLamports, 
  handleError,
  PROGRAM_ID,
  AUCTION_SEED,
  DAY_SEED,
  USER_SEED,
  SOL_VAULT_SEED
} from './baseService';

class AuctionService {
  constructor() {
    this.connection = null;
    this.program = null;
    this.wallet = null;
    this.initialized = false;
    this.auctionPDA = null;
    this.tokenMint = null;
  }

  /**
   * Initialize the auction service
   * @param {Connection} connection - Solana connection
   * @param {Object} wallet - Solana wallet
   * @param {string} tokenMint - JINU token mint address
   * @returns {boolean} - Whether initialization was successful
   */
  async initialize(connection, wallet, tokenMint) {
    try {
      this.connection = connection;
      this.wallet = wallet;
      this.tokenMint = new PublicKey(tokenMint);
      
      // Load the program
      this.program = await getProgram(connection, wallet);
      
      // Find the auction PDA
      const [auctionPDA, auctionBump] = await findPDA([
        Buffer.from(AUCTION_SEED),
        this.tokenMint.toBuffer()
      ]);
      
      this.auctionPDA = auctionPDA;
      this.auctionBump = auctionBump;
      
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize auction service:', error);
      return false;
    }
  }

  /**
   * Initialize the auction
   * @param {number} startTimestamp - Auction start timestamp in seconds
   * @returns {Promise<string>} - Transaction signature
   */
  async initializeAuction(startTimestamp) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      // Find the SOL vault PDA
      const [solVaultPDA, solVaultBump] = await findPDA([
        Buffer.from(SOL_VAULT_SEED),
        this.auctionPDA.toBuffer()
      ]);

      // Call the initialize_auction instruction
      const tx = await this.program.methods
        .initializeAuction(
          new BN(startTimestamp),
          this.auctionBump
        )
        .accounts({
          authority: this.wallet.publicKey,
          auction: this.auctionPDA,
          tokenMint: this.tokenMint,
          solVault: solVaultPDA,
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
          rent: SYSVAR_RENT_PUBKEY
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Initialize a day allocation
   * @param {number} day - Day number (1-50)
   * @returns {Promise<string>} - Transaction signature
   */
  async initializeDay(day) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      // Find the day allocation PDA
      const [dayPDA, dayBump] = await findPDA([
        Buffer.from(DAY_SEED),
        this.auctionPDA.toBuffer(),
        Buffer.from([day])
      ]);

      // Call the initialize_day instruction
      const tx = await this.program.methods
        .initializeDay(
          day,
          dayBump
        )
        .accounts({
          authority: this.wallet.publicKey,
          auction: this.auctionPDA,
          dayAllocation: dayPDA,
          systemProgram: SystemProgram.programId,
          rent: SYSVAR_RENT_PUBKEY
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get auction state
   * @returns {Promise<Object>} - Auction state
   */
  async getAuctionState() {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      const auctionState = await this.program.account.auctionState.fetch(this.auctionPDA);
      return auctionState;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get day allocation
   * @param {number} day - Day number (1-50)
   * @returns {Promise<Object>} - Day allocation
   */
  async getDayAllocation(day) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      // Find the day allocation PDA
      const [dayPDA] = await findPDA([
        Buffer.from(DAY_SEED),
        this.auctionPDA.toBuffer(),
        Buffer.from([day])
      ]);

      const dayAllocation = await this.program.account.dayAllocation.fetch(dayPDA);
      return dayAllocation;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get all day allocations
   * @returns {Promise<Array>} - Array of day allocations
   */
  async getAllDayAllocations() {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      const allocations = [];
      for (let day = 1; day <= 50; day++) {
        try {
          const allocation = await this.getDayAllocation(day);
          allocations.push({
            day,
            allocation
          });
        } catch (error) {
          // Day might not be initialized yet
          allocations.push({
            day,
            allocation: null
          });
        }
      }
      return allocations;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Get user contribution
   * @param {PublicKey} userPubkey - User public key
   * @returns {Promise<Object>} - User contribution
   */
  async getUserContribution(userPubkey) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      // Find the user contribution PDA
      const [userPDA] = await findPDA([
        Buffer.from(USER_SEED),
        this.auctionPDA.toBuffer(),
        userPubkey.toBuffer()
      ]);

      const userContribution = await this.program.account.userContribution.fetch(userPDA);
      return userContribution;
    } catch (error) {
      // User might not have contributed yet
      return null;
    }
  }

  /**
   * Update auction status
   * @param {boolean} isActive - Whether the auction is active
   * @returns {Promise<string>} - Transaction signature
   */
  async updateAuctionStatus(isActive) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      const tx = await this.program.methods
        .updateAuctionStatus(isActive)
        .accounts({
          authority: this.wallet.publicKey,
          auction: this.auctionPDA
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Setup liquidity pool
   * @returns {Promise<string>} - Transaction signature
   */
  async setupLiquidity() {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      // Find the SOL vault PDA
      const [solVaultPDA] = await findPDA([
        Buffer.from(SOL_VAULT_SEED),
        this.auctionPDA.toBuffer()
      ]);

      const tx = await this.program.methods
        .setupLiquidity()
        .accounts({
          authority: this.wallet.publicKey,
          auction: this.auctionPDA,
          solVault: solVaultPDA,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: SystemProgram.programId
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Distribute unused funds
   * @param {PublicKey} devWallet - Developer wallet public key
   * @returns {Promise<string>} - Transaction signature
   */
  async distributeUnused(devWallet) {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      const tx = await this.program.methods
        .distributeUnused()
        .accounts({
          authority: this.wallet.publicKey,
          auction: this.auctionPDA,
          devWallet: devWallet,
          tokenProgram: TOKEN_PROGRAM_ID
        })
        .rpc();

      return tx;
    } catch (error) {
      throw new Error(handleError(error));
    }
  }

  /**
   * Calculate current day based on auction start date
   * @returns {Promise<number>} - Current day (1-50)
   */
  async calculateCurrentDay() {
    if (!this.initialized) {
      throw new Error('Auction service not initialized');
    }

    try {
      const auctionState = await this.getAuctionState();
      const startDate = auctionState.startDate.toNumber();
      const now = Math.floor(Date.now() / 1000);
      
      if (now < startDate) {
        return 1; // Auction hasn't started yet
      }
      
      const dayDuration = 24 * 60 * 60; // 24 hours in seconds
      const elapsedDays = Math.floor((now - startDate) / dayDuration) + 1;
      
      return Math.min(elapsedDays, 50); // Cap at 50 days
    } catch (error) {
      throw new Error(handleError(error));
    }
  }
}

// Create a singleton instance
const auctionService = new AuctionService();
export default auctionService;
